import React, { useState, useEffect } from 'react';
import './Order.css';

import { FaUser, FaEnvelope, FaPhone, FaList, FaCalendar, FaComment, FaAngleDown } from 'react-icons/fa';
import axios from "axios";

import { Heading } from '../../../components';

import { FaLocationDot } from 'react-icons/fa6';

// API endpoint for order submission
const API_PATH = 'https://www.example.com/api/phpmailer/order.php';

// Validation regex patterns
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const Order = () => {

  // Form state management
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    plan: '',
    date: '',
    address: '',
    query: ''
  });

  // Submission status states
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Field validation
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.plan) validationErrors.push('Plan selection is required');
      if (!formData.date) validationErrors.push('Preferred date is required');
      if (!formData.address) validationErrors.push('Address is required');
      if (!formData.query) validationErrors.push('Query is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare and submit form data
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // Reset form on success
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        plan: '',
        date: '',
        address: '',
        query: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="custom-form order-form linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Request an Order"} subTitle={"Order Your Service"} linear sideHeading />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Choose Your Plan Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="plan"><FaList /></label>
                  <select
                    className="select-box"
                    id="plan"
                    name="plan"
                    value={formData.plan}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Choose Your Plan</option>
                    <option value="Internet Only">Internet Only Plan</option>
                    <option value="Cable TV Only">Cable TV Only Plan</option>
                    <option value="Internet + Cable TV">Internet + Cable TV Bundle</option>
                    <option value="Broadband">Broadband Plan</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">
              {/* Preferred Date Input Field */}
              <div className="input-field">
                <label htmlFor="date"><FaCalendar /></label>
                <input
                  type="text"
                  name="date"
                  placeholder="Preferred Date"
                  id="date"
                  className="box"
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Address Input Field */}
              <div className="input-field">
                <label htmlFor="address"><FaLocationDot /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Installation Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Any Query Text Area */}
            <div className="input-field text-area">
              <label htmlFor="query"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="query"
                className="box"
                id="query"
                placeholder="Any Queries or Special Requests"
                value={formData.query}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button for Order Confirmation */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Order'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Order Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default Order;